import React, { useEffect } from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { CgSpinner } from 'react-icons/cg';

import useDebounce from 'CustomHooks/useDebounce';
import api from 'http';
import useSuperSearchDropdownContext from 'NewComponents/FormComponents/SuperSearchDropdown/useSuperSearchDropdownContext';
import { ErrorHandlingMessage } from 'Utils/ErrorHandlingMessage';

const PayerSuperSearchInput = ({ searchValue, onChange, handleSelectedData, identifier, disabled, placeholder = 'Search Payer...' }) => {
  const { onToggleLoader, onToggleDropDown, onSetData, onSetInputValue, onSetSearchStatus, onSetSelectedRowNumber, state } = useSuperSearchDropdownContext();
  const debouncedSearchTerm = useDebounce(searchValue, 500);
  const { data, loading, searchStatus, selectedRowNumber } = state;

  const resetComponent = () => {
    onChange('');
    handleSelectedData();
    onSetInputValue('');
    onSetSearchStatus(false);
    onSetSelectedRowNumber(0);
    onSetData([]);
    onToggleDropDown(false);
  };

  const onKeyDownPressed = async (event_) => {
    switch (event_.keyCode) {
      case 13: {
        if (data.length > 0) {
          await handleSelectedData(data[selectedRowNumber], event_);
          onSetSearchStatus(false);
          onSetInputValue('');
          onToggleDropDown(false);
        }
        break;
      }
      case 38: {
        if (selectedRowNumber > 0) {
          const previousRowNumber = selectedRowNumber - 1;
          onSetSelectedRowNumber(previousRowNumber);
          const element = document.querySelector(`#${identifier}_${previousRowNumber}`);
          element?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
        break;
      }
      case 40: {
        if (selectedRowNumber < data.length - 1) {
          const nextRowNumber = selectedRowNumber + 1;
          onSetSelectedRowNumber(nextRowNumber);
          const element = document.querySelector(`#${identifier}_${nextRowNumber}`);
          element?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
        break;
      }
      case 27: {
        resetComponent();
        break;
      }
      case 9: {
        if (data.length > 0) {
          handleSelectedData(data[selectedRowNumber]);
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  const onHandleChange = (event_) => {
    const eventValue = event_.target.value;
    onChange(eventValue);
    onSetInputValue(eventValue);

    if (eventValue === '') {
      handleSelectedData();
      onSetSearchStatus(false);
      onSetSelectedRowNumber(0);
      onSetData([]);
      onToggleDropDown(false);
    } else {
      onSetSearchStatus(true);
    }
  };

  const handleApiCall = React.useCallback(
    async (searchTerm) => {
      const response = await api.post(`${process.env.REACT_APP_TC_AUTH}/api/InsurancePlan/FindInsurancePlanAdvance/`, {
        param_list: [{ key: 'PLANNAME', value: searchTerm }],
        practiceId: 0,
        perPage: 10,
        pageNo: 1,
        totalPages: 0,
        totalCount: 0,
        isExportCall: false,
      });

      const list = response?.data?.dataModel?.data ?? response?.data?.products ?? response?.data?.dataModel ?? [];
      onSetData(Array.isArray(list) ? list : []);
      onToggleDropDown(true);
    },
    [onSetData, onToggleDropDown],
  );

  useEffect(() => {
    if (searchStatus && debouncedSearchTerm.length >= 2) {
      (async () => {
        try {
          onToggleLoader(true);
          await handleApiCall(debouncedSearchTerm);
        } catch (error) {
          ErrorHandlingMessage(error);
          onSetData([]);
          onToggleDropDown(true);
        } finally {
          onToggleLoader(false);
        }
      })();
    }
  }, [searchStatus, debouncedSearchTerm, onToggleLoader, handleApiCall, onSetData, onToggleDropDown]);

  return (
    <div className='relative'>
      <div className='absolute left-2 top-1/2 -translate-y-1/2 transform'>
        <FiSearch size={14} className='text-[#8D8D8D]' />
      </div>
      <input
        disabled={disabled}
        onChange={onHandleChange}
        value={searchValue}
        id={`superSearchInputField_${identifier}`}
        type='text'
        autoComplete='off'
        className='h-9 w-full rounded-[0.25rem] border  py-1.5  px-7 text-xs focus:outline-none  focus:border-primary disabled:bg-gray-100'
        placeholder={placeholder}
        onKeyDown={onKeyDownPressed}
      />
      {searchValue ? (
        <button type='button' disabled={disabled} className='absolute right-2 top-1/2 -translate-y-1/2 transform disabled:cursor-not-allowed' onClick={resetComponent}>
          {loading ? <CgSpinner className='animate-spin' /> : <FiX size={14} className='cursor-pointer text-[#8D8D8D]' />}
        </button>
      ) : null}
    </div>
  );
};

export default PayerSuperSearchInput;
